import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import NumberInput from "@/components/NumberInput";

export interface SetupValues {
  rotation: number;
  brickWidthMm: number | null;
  brickHeightMm: number | null;
}

interface Props {
  values: SetupValues;
  detected: boolean;
  changed: boolean;
  onChange(values: SetupValues): void;
  onCommit(): void;
  onRedetect(): void;
  guides: boolean;
  onGuides(guides: boolean): void;
}

const SetupPanel: React.FC<Props> = ({
  values,
  detected,
  changed,
  guides,
  onChange,
  onCommit,
  onRedetect,
  onGuides,
}) => {
  const complete = values.brickWidthMm != null && values.brickHeightMm != null;
  const commitOnEnter = (e: React.KeyboardEvent) => e.key === "Enter" && onCommit();
  return (
    <section className="grid gap-4">
      <div className="grid gap-2">
        <h2 className="text-sm font-semibold">Hele steen op deze foto</h2>
        <div className="grid grid-cols-2 gap-2">
          <NumberInput
            id="brick-width"
            aria-label="Breedte van een hele steen in mm"
            placeholder="Breedte"
            suffix="mm"
            min={10}
            max={1000}
            value={values.brickWidthMm}
            onChange={(brickWidthMm) => onChange({ ...values, brickWidthMm })}
            onBlur={onCommit}
            onKeyDown={commitOnEnter}
          />
          <NumberInput
            id="brick-height"
            aria-label="Hoogte van een hele steen in mm"
            placeholder="Hoogte"
            suffix="mm"
            min={10}
            max={1000}
            value={values.brickHeightMm}
            onChange={(brickHeightMm) => onChange({ ...values, brickHeightMm })}
            onBlur={onCommit}
            onKeyDown={commitOnEnter}
          />
        </div>
        {!detected && !complete && (
          <p className="text-sm text-muted-foreground">
            Vul de maat van een hele steen in om de stenen te detecteren.
          </p>
        )}
      </div>
      <div className="grid gap-2">
        <h2 className="text-sm font-semibold">Rechtzetten</h2>
        <div className="flex items-center gap-3">
          <Slider
            aria-label="Rotatie"
            min={-5}
            max={5}
            step={0.1}
            value={[values.rotation]}
            onValueChange={([rotation]) => onChange({ ...values, rotation })}
          />
          <NumberInput
            id="rotation"
            aria-label="Rotatie in graden"
            className="w-24 shrink-0"
            suffix="°"
            min={-5}
            max={5}
            decimals={1}
            value={values.rotation}
            onChange={(rotation) => rotation != null && onChange({ ...values, rotation })}
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-guides"
            checked={guides}
            onCheckedChange={(checked) => onGuides(checked === true)}
          />
          <Label htmlFor="show-guides" className="font-normal">
            Horizontale hulplijnen tonen
          </Label>
        </div>
      </div>
      {detected && (
        <Button onClick={onRedetect} disabled={!complete} variant={changed ? "default" : "outline"}>
          Stenen opnieuw detecteren
        </Button>
      )}
    </section>
  );
};

export default SetupPanel;
